import { useEffect, useRef, useState } from "react";

import Spinner from "../../../shared/ui/Loading/Spinner";
import EmptyState from "../../../shared/ui/EmptyState/EmptyState";
import { messageFromError } from "../../../shared/api/drfErrors";

import CalendarTaskEditorModal from "../../tasksShared/widgets/TaskEditorModal";
import {
  getPriorityLabel,
  getStatusLabel,
  formatDateTime,
} from "../../tasksList/lib/taskFormatting";
import { isRequestAbortError } from "../api/deals";
import { fetchTasksForDeal, unwrapTaskListPayload } from "../api/dealTasks";
import DealWorkspaceSectionHeader from "./DealWorkspaceSectionHeader";

/**
 * D2-C1 — private Tasks section for a Deal party.
 * Lists only the current user's Tasks for this Deal; never discloses
 * counterparty Task existence via counts or empty-copy.
 */
function DealPrivateTasksSection({ dealId }) {
  const [loadState, setLoadState] = useState("loading");
  const [loadError, setLoadError] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [retryToken, setRetryToken] = useState(0);
  const [editorOpen, setEditorOpen] = useState(false);
  const mutationGenerationRef = useRef(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      mutationGenerationRef.current += 1;
    };
  }, []);

  useEffect(() => {
    if (dealId == null || dealId === "") {
      setLoadState("idle");
      setTasks([]);
      return undefined;
    }

    const controller = new AbortController();
    setLoadState("loading");
    setLoadError(null);

    fetchTasksForDeal(dealId, { signal: controller.signal })
      .then((data) => {
        if (controller.signal.aborted || !mountedRef.current) return;
        setTasks(unwrapTaskListPayload(data));
        setLoadState("loaded");
      })
      .catch((error) => {
        if (controller.signal.aborted || isRequestAbortError(error)) return;
        if (!mountedRef.current) return;
        setTasks([]);
        setLoadError(messageFromError(error));
        setLoadState("error");
      });

    return () => controller.abort();
  }, [dealId, retryToken]);

  const handleRetry = () => {
    setRetryToken((token) => token + 1);
  };

  const handleOpenCreate = () => {
    setEditorOpen(true);
  };

  const handleCloseEditor = () => {
    setEditorOpen(false);
  };

  const handleSaved = () => {
    setEditorOpen(false);
    // Authoritative refetch once after successful create.
    setRetryToken((token) => token + 1);
  };

  return (
    <section
      className="feature-deal-detail__section"
      aria-labelledby="deal-tasks-heading"
      data-testid="deal-private-tasks-section"
    >
      <DealWorkspaceSectionHeader
        id="deal-tasks-heading"
        eyebrow="Особиста робота"
        title="Задачі по угоді"
        helper="Ці задачі бачите лише ви. Контекст об'єкта надходить через угоду."
        iconClassName="fa-solid fa-list-check"
        actions={(
          <button
            type="button"
            className="action-btn"
            onClick={handleOpenCreate}
            data-testid="deal-create-task-button"
          >
            Створити задачу
          </button>
        )}
      />

      {loadState === "loading" ? (
        <Spinner size="md" label="Завантаження особистих задач по угоді" />
      ) : null}

      {loadState === "error" ? (
        <div role="alert">
          <p>{loadError || "Не вдалося завантажити особисті задачі по угоді."}</p>
          <button type="button" className="action-btn" onClick={handleRetry}>
            Спробувати ще
          </button>
        </div>
      ) : null}

      {loadState === "loaded" && tasks.length === 0 ? (
        <EmptyState
          className="feature-deal-detail__empty-compact"
          size="sm"
          icon="fa-solid fa-list-check"
          title="Немає особистих задач по угоді"
          description="У вас ще немає особистих задач для цієї угоди."
        />
      ) : null}

      {loadState === "loaded" && tasks.length > 0 ? (
        <ul className="feature-deal-detail__timeline" data-testid="deal-private-tasks-list">
          {tasks.map((task) => (
            <li
              key={task.id}
              className="feature-deal-detail__timeline-item"
              data-testid={`deal-task-row-${task.id}`}
            >
              <div className="feature-deal-detail__timeline-title">{task.title}</div>
              <div className="feature-deal-detail__timeline-meta">
                <span>{getStatusLabel(task.status)}</span>
                <span>· {getPriorityLabel(task.priority)}</span>
                {task.due_date ? (
                  <span>· <time>{formatDateTime(task.due_date)}</time></span>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      ) : null}

      <CalendarTaskEditorModal
        open={editorOpen}
        mode="create"
        task={null}
        prefillDay={null}
        fixedDealId={dealId}
        onClose={handleCloseEditor}
        onSaved={handleSaved}
        mutationGenerationRef={mutationGenerationRef}
      />
    </section>
  );
}

export default DealPrivateTasksSection;
